import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import GetAppIcon from '@mui/icons-material/GetApp';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningIcon from '@mui/icons-material/Warning';
import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Helmet } from 'react-helmet-async';
import { Button, Table } from 'src/common';
import { getPageTitle } from 'src/config/projectConfig';
import { SECURITY_PAGE_FILTER_KEYS } from 'src/mindtime/constants/securityFilters';
import {
  getSecurityAutomatedResponseDetailHref,
  getSecurityEndpointDetailsHref,
  getSecurityNavItemPath,
  SECURITY_ENDPOINTS_HREF,
} from 'src/mindtime/config/securityNav';
import { EDR_SOLUTION_ID } from 'src/mindtime/constants/securitySolutions';
import { useSecurityEdrToolbarReset, useSecurityPageFilters } from 'src/mindtime/hooks/useSecurityPageFilters';
import { useSubscriptionModulePermissions } from 'src/mindtime/hooks/useSubscriptionPermissionAccess';
import { useWazuhScope } from 'src/mindtime/hooks/useWazuhScope';
import type { Column, TableData } from 'src/types/table';
import type { WazuhActiveResponseEndpointItem, WazuhActiveResponseItem } from 'src/types/mindtime/wazuhActiveResponse';
import {
  ACTIVE_RESPONSE_SEVERITY_FILTER_OPTIONS,
  ACTIVE_RESPONSE_STATUS_FILTER_OPTIONS,
  ActiveResponseSummaryKpiGrid,
  getActiveResponseAffectedEndpointListColumns,
  getActiveResponseId,
  getActiveResponseListColumns,
} from './automatedResponseDetailModel';
import { useActiveResponseActionLabelOptions } from './useActiveResponseActionLabelOptions';
import { useAutomatedResponseData, useAutomatedResponseExport } from './useAutomatedResponseData';
import {
  EDR_COLORS,
  EdrDataCard,
  EdrDataToolbar,
  EdrGlobalScopeFilters,
  EdrPageHeader,
  SubscriptionUnavailableMessage,
  type EdrToolbarFilter,
  formatEdrNumber,
}  from '../shared/edrPrimitives';

const breadcrumb = {
  links: [
    { name: 'EDR', to: SECURITY_ENDPOINTS_HREF },
    { name: 'Automated Response', to: getSecurityNavItemPath('automated-response') ?? '' },
  ],
};


const AutomatedResponse: FC = () => {
  const navigate = useNavigate();
  const { dateRange, setDateRange, setSelectedEndpointId, selectedClientId } = useWazuhScope();
  const { filters, setFilter, search, searchInput, setSearchInput, resetFilters } =
    useSecurityPageFilters(SECURITY_PAGE_FILTER_KEYS.automatedResponse);
  const {
    status = '',
    actionLabel = '',
    severityBand = '',
  } = filters;
  const organizationId = selectedClientId ?? '';
  const { canWidget, permissionsResolved } = useSubscriptionModulePermissions(
    EDR_SOLUTION_ID,
    'automated-response',
  );
  const canSummary = canWidget('summary');
  const canList = canWidget('list');
  const canAffectedEndpoints = canWidget('affectedEndpoints');
  const showUnavailable =
    permissionsResolved && !canSummary && !canList && !canAffectedEndpoints;
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const responseFilters = useMemo(
    () => ({
      status: filters.status,
      actionLabel: filters.actionLabel,
      severityBand: filters.severityBand,
      organizationId,
    }),
    [filters.status, filters.actionLabel, filters.severityBand, organizationId]
  );

  const { options: actionLabelOptions } = useActiveResponseActionLabelOptions({
    pageOrganizationId: organizationId || undefined,
    dateRange,
  });

  const {
    loading,
    summary,
    items: paged,
    endpoints: endpointsPaged,
  } = useAutomatedResponseData(page, rowsPerPage, search, responseFilters);
  const { exporting, handleExport } = useAutomatedResponseExport(search, responseFilters);

  const items = paged?.items ?? [];
  const totalRecords = paged?.totalRecords ?? 0;
  const affectedEndpoints = endpointsPaged?.items ?? [];
  const affectedEndpointsTotal = endpointsPaged?.totalRecords ?? 0;

  const handleResetFilters = useSecurityEdrToolbarReset(
    resetFilters,
    setDateRange,
    setPage,
    setSelectedEndpointId
  );

  const openRowDetails = useCallback(
    (item: WazuhActiveResponseItem) => {
      const id = getActiveResponseId(item);
      if (!id) return;
      navigate(getSecurityAutomatedResponseDetailHref(id), { state: { record: item } });
    },
    [navigate]
  );

  const toolbarFilters: EdrToolbarFilter[] = useMemo(
    () => [
      {
        id: 'actionLabel',
        label: 'Action Type',
        icon: <FlashOnIcon style={{ fontSize: 14 }} />,
        options: actionLabelOptions,
        value: actionLabel,
        multiple: true,
        onChange: (value) => {
          setFilter('actionLabel', value);
          setPage(0);
        },
      },
      {
        id: 'status',
        label: 'Status',
        icon: <TrendingUpIcon style={{ fontSize: 14 }} />,
        options: ACTIVE_RESPONSE_STATUS_FILTER_OPTIONS,
        value: status,
        multiple: true,
        onChange: (value) => {
          setFilter('status', value);
          setPage(0);
        },
      },
      {
        id: 'severity',
        label: 'Severity',
        icon: <WarningIcon style={{ fontSize: 14 }} />,
        options: ACTIVE_RESPONSE_SEVERITY_FILTER_OPTIONS,
        value: severityBand,
        multiple: true,
        onChange: (value) => {
          setFilter('severityBand', value);
          setPage(0);
        },
      },
    ],
    [
      actionLabel,
      actionLabelOptions,
      status,
      severityBand,
      setFilter,
    ]
  );

  const columns: Column[] = useMemo(
    () => getActiveResponseListColumns('global', openRowDetails),
    [openRowDetails]
  );
  const affectedEndpointColumns: Column[] = useMemo(
    () => getActiveResponseAffectedEndpointListColumns(),
    []
  );

  const affectedEndpointsToolbar = (
    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
      <Typography style={{ fontSize: 13, fontWeight: 700, color: EDR_COLORS.textPrimary }}>
        Endpoints Targeted by Automated Response
      </Typography>
      <Typography style={{ fontSize: 12, color: EDR_COLORS.textMuted }}>
        {formatEdrNumber(affectedEndpointsTotal)} endpoints affected
      </Typography>
    </Box>
  );

  return (
    <>
      <Helmet>
        <title>{getPageTitle('Security — Automated Response')}</title>
      </Helmet>

      <Box
        p={3}
        display="flex"
        flexDirection="column"
        style={{ gap: 16, minHeight: '100%', boxSizing: 'border-box' }}
      >
        <EdrPageHeader
          breadcrumb={breadcrumb}
          title="Automated Response"
          subtitle="Review and monitor automated response actions executed across your environment."
        />
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          style={{ gap: 12 }}
        >
          <EdrGlobalScopeFilters
            pageOrganizationId={organizationId || undefined}
            onChange={() => setPage(0)}
          />
          {canList ? (
            <Button
              variant="contained"
              color="primary"
              startIcon={<GetAppIcon />}
              style={{ textTransform: 'none' }}
              loading={exporting}
              onClick={handleExport}
            >
              Export
            </Button>
          ) : null}
        </Box>

        {showUnavailable ? (
          <SubscriptionUnavailableMessage messageKey="unavailable.automatedResponse" />
        ) : null}

        {canSummary ? <ActiveResponseSummaryKpiGrid summary={summary} /> : null}

        {canAffectedEndpoints ? (
          <EdrDataCard toolbar={affectedEndpointsToolbar}>
            <Table
              columns={affectedEndpointColumns}
              data={affectedEndpoints as unknown as TableData[]}
              loading={loading}
              showPagination={false}
              onRowClick={(row) => {
                const item = row as unknown as WazuhActiveResponseEndpointItem;
                const systemWazuhAgentId = item.systemWazuhAgentId?.trim();
                if (!systemWazuhAgentId) return;
                navigate(getSecurityEndpointDetailsHref(systemWazuhAgentId), {
                  state: { endpointName: item.endpoint ?? item.name },
                });
              }}
              TableContainerProps={{ sx: { boxShadow: 'none', borderRadius: 0 } }}
            />
          </EdrDataCard>
        ) : null}

        {canList ? (
          <EdrDataCard
            toolbar={
              <EdrDataToolbar
                searchValue={searchInput}
                onSearchChange={(value) => {
                  setSearchInput(value);
                  setPage(0);
                }}
                searchPlaceholder="Search responses…"
                filters={toolbarFilters}
                onReset={handleResetFilters}
                resetLabel="Reset Filters"
              />
            }
          >
            <Table
              columns={columns}
              data={items as unknown as TableData[]}
              loading={loading}
              showPagination
              page={page}
              rowsPerPage={rowsPerPage}
              totalRows={totalRecords}
              onPageChange={setPage}
              onRowsPerPageChange={(next) => {
                setRowsPerPage(next);
                setPage(0);
              }}
              onRowClick={(row) => openRowDetails(row as unknown as WazuhActiveResponseItem)}
              TableContainerProps={{ sx: { boxShadow: 'none', borderRadius: 0 } }}
            />
          </EdrDataCard>
        ) : null}
      </Box>
    </>
  );
};

export default AutomatedResponse;
